import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import CalendarioLegenda from 'components/Relatorios/shared/Aprovacao/CalendarioLegenda';
import useCalendarioAprovacao from 'hooks/Relatorios/Aprovacao/useCalendarioAprovacao';
import {
  selectDecompDataAnalisada,
  selectDecompCenario,
  selectDecompModo,
  setDataAnalisada,
  setCenario,
  setModo,
} from 'redux/reducers/relatoriosDecompReducer';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { DecompModo } from 'types/Relatorios/Decomp';

import './index.scss';

const config = getAprovacaoConfig('decomp');

const MODOS: { value: DecompModo; label: string }[] = (
  Object.keys(config.variante.labels) as DecompModo[]
).map((value) => ({ value, label: config.variante.labels[value] }));

/**
 * Painel de controle do Informe DECOMP. Segue o padrão do sistema (ex.: numeric-
 * forecast): `Accordion` do design system (fundo cinza, seções colapsáveis) dentro
 * do `RightSidebar`, com o `DatePicker` inline para a Data Analisada. Comportamento
 * travado: rascunho local (useState) que só faz commit ao slice no clique em
 * "Atualizar Data/Cenário"; a navegação por abas é imediata.
 *
 * Com a aprovação habilitada, o calendário ganha bolinhas (admin) ou só permite dias
 * publicados (cliente), consultando o **modo aplicado**. O Cenário (texto livre) fica
 * fora da chave da aprovação (D2). D8: a data do rascunho acompanha a data aplicada;
 * modo/cenário do rascunho não são ressincronizados.
 */
export default function DecompControlPanel() {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectDecompDataAnalisada);
  const cenarioAplicado = useSelector(selectDecompCenario);
  const modoAplicado = useSelector(selectDecompModo);

  const [draftData, setDraftData] = useState(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
  );
  const [draftCenario, setDraftCenario] = useState(cenarioAplicado);
  const [draftModo, setDraftModo] = useState<DecompModo>(modoAplicado);

  const {
    datePickerProps, mostraLegenda, onDataSelecionada,
  } = useCalendarioAprovacao(config, dataAnalisada, modoAplicado);

  const { ano, mes, dia } = dataAnalisada;
  useEffect(() => {
    setDraftData((atual) => (
      atual.getFullYear() === ano && atual.getMonth() === mes - 1 && atual.getDate() === dia
        ? atual
        : new Date(ano, mes - 1, dia)
    ));
  }, [ano, mes, dia]);

  const handleDataChange = useCallback((date: Date | null) => {
    if (!date) return;
    setDraftData(date);
    onDataSelecionada(date);
  }, [onDataSelecionada]);

  const handleCenarioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftCenario(e.target.value);
  }, []);

  const handleModoChange = useCallback((value: DecompModo) => () => {
    setDraftModo(value);
  }, []);

  const handleAtualizar = useCallback(() => {
    dispatch(setDataAnalisada({
      mes: draftData.getMonth() + 1,
      ano: draftData.getFullYear(),
      dia: draftData.getDate(),
    }));
    dispatch(setCenario(draftCenario));
    dispatch(setModo(draftModo));
  }, [dispatch, draftData, draftCenario, draftModo]);

  return (
    <div className="decomp-control-panel">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data Analisada</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={draftData}
              onChange={handleDataChange}
              maxDate={new Date()}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...datePickerProps}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid">
            <input
              id="decomp-cenario"
              type="text"
              aria-label="Cenário"
              value={draftCenario}
              onChange={handleCenarioChange}
              placeholder="Ex: PMO"
              className="decomp-control-panel__input"
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>Modo</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {MODOS.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="decomp-modo"
                label={label}
                value={value}
                checked={draftModo === value}
                onChange={handleModoChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button onClick={handleAtualizar} className="decomp-control-panel__atualizar">
        Atualizar Data/Cenário
      </Button>

      {mostraLegenda && <CalendarioLegenda isAdmin />}
    </div>
  );
}
